'use client';

import { fetcher } from '@insta-club/api';
import { ArchiveFillIcon, ArchiveIcon, HeartFillIcon, HeartIcon } from '@insta-club/components';
import { cn } from '@insta-club/utilities';
import { useEffect, useMemo, useState } from 'react';
import useSWRMutation from 'swr/mutation';

type ToggleStatus = 'like' | 'mark';
type BuildEndpoint = (id: string, status: ToggleStatus) => string;

interface LikeMarkActionsProps {
    id: string;
    liked: boolean;
    marked: boolean;
    buildEndpoint: BuildEndpoint;
    className?: string;
    disabled?: boolean;
    size?: 'sm' | 'md';
    onChange?: (next: { liked: boolean; marked: boolean }) => void;
}

async function toggleEntityStatus(url: string, { arg }: { arg: { id: string; status: ToggleStatus } }) {
    return await fetcher(url, 'dashboard', 'GET', { params: arg });
}

export default function LikeMarkActions({
    id,
    liked,
    marked,
    buildEndpoint,
    className,
    disabled = false,
    size = 'md',
    onChange
}: LikeMarkActionsProps) {
    const [isLiked, setIsLiked] = useState(!!liked);
    const [isMarked, setIsMarked] = useState(!!marked);

    useEffect(() => setIsLiked(!!liked), [liked]);
    useEffect(() => setIsMarked(!!marked), [marked]);

    const likeKey = useMemo(() => buildEndpoint(id, 'like'), [buildEndpoint, id]);
    const markKey = useMemo(() => buildEndpoint(id, 'mark'), [buildEndpoint, id]);

    const { trigger: toggleLike, isMutating: likeLoading } = useSWRMutation(likeKey, toggleEntityStatus);
    const { trigger: toggleMark, isMutating: markLoading } = useSWRMutation(markKey, toggleEntityStatus);

    const busy = disabled || likeLoading || markLoading;

    const handleToggle = async (type: ToggleStatus) => {
        if (busy) return;

        if (type === 'like') {
            const next = !isLiked;
            setIsLiked(next);
            onChange?.({ liked: next, marked: isMarked });
            try {
                await toggleLike({ id, status: 'like' });
            } catch {
                setIsLiked(!next);
                onChange?.({ liked: !next, marked: isMarked });
            }
            return;
        }

        if (type === 'mark') {
            const next = !isMarked;
            setIsMarked(next);
            onChange?.({ liked: isLiked, marked: next });
            try {
                await toggleMark({ id, status: 'mark' });
            } catch {
                setIsMarked(!next);
                onChange?.({ liked: isLiked, marked: !next });
            }
            return;
        }
    };

    return (
        <div className={cn('flex items-center space-x-3.5', className)}>
            <button
                type='button'
                disabled={busy}
                onClick={() => handleToggle('like')}
                className={cn('relative transition', {
                    'animate-pulse opacity-50': likeLoading,
                    'cursor-not-allowed': busy
                })}
                aria-label={isLiked ? 'لغو لایک' : 'لایک کردن'}
            >
                {isLiked ? (
                    <HeartFillIcon
                        className={cn('text-primary-500', {
                            'h-5 w-5': size === 'sm',
                            'h-6 w-6': size === 'md'
                        })}
                    />
                ) : (
                    <HeartIcon
                        className={cn('text-gray-500', {
                            'h-5 w-5': size === 'sm',
                            'h-6 w-6': size === 'md'
                        })}
                    />
                )}
            </button>

            <button
                type='button'
                disabled={busy}
                onClick={() => handleToggle('mark')}
                className={cn('relative transition', {
                    'animate-pulse opacity-50': markLoading,
                    'cursor-not-allowed': busy
                })}
                aria-label={isMarked ? 'حذف از نشان‌شده‌ها' : 'نشان/آرشیو کردن'}
            >
                {isMarked ? (
                    <ArchiveFillIcon
                        className={cn('text-primary-500', {
                            'h-5 w-5': size === 'sm',
                            'h-6 w-6': size === 'md'
                        })}
                    />
                ) : (
                    <ArchiveIcon
                        className={cn('text-gray-500', {
                            'h-5 w-5': size === 'sm',
                            'h-6 w-6': size === 'md'
                        })}
                    />
                )}
            </button>
        </div>
    );
}

